import { useGetIdentity } from "ra-core";
import { PRODUCT_FULL_NAME, PRODUCT_NAME } from "@/lib/branding";
import type { BillingEmailBrand } from "../../supabase/functions/_shared/billingEmailLayout.ts";
import { getBillingEmailBrand } from "../../supabase/functions/_shared/invoiceOrganizationInfo.ts";

/** The member's company, loaded with the identity (authProvider). */
export type WorkspaceIdentity = {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  website: string;
  /** The platform's home org (LBS) keeps "Sigma by Latino Business Support". */
  is_home: boolean;
};

/**
 * Current workspace for plain (non-React) helpers: signatures, templates,
 * invoice copy. Set from the member identity (authProvider). Defaults to the
 * home org so nothing changes before the identity loads.
 */
export type CurrentWorkspace = {
  isHome: boolean;
  name: string;
  email: string;
  phone: string;
  address: string;
  website: string;
  /** Public https logo for client emails (Company profile → Branding). */
  emailLogoUrl?: string | null;
};

let currentWorkspace: CurrentWorkspace = {
  isHome: true,
  name: "",
  email: "",
  phone: "",
  address: "",
  website: "",
};

export const setCurrentWorkspace = (workspace: CurrentWorkspace) => {
  currentWorkspace = workspace;
};

export const getCurrentWorkspace = () => currentWorkspace;

/**
 * Header/footer of client emails built in the CRM (previews and sends).
 * Mirrors supabase/functions/_shared/orgEmailBrand.ts.
 */
export const workspaceEmailBrand = (
  orgName?: string | null,
): BillingEmailBrand => {
  const workspace = getCurrentWorkspace();
  if (workspace.isHome) {
    return getBillingEmailBrand(orgName);
  }
  return {
    name: workspace.name || orgName?.trim() || "",
    logoUrl: workspace.emailLogoUrl ?? null,
    bannerUrl: null,
    phone: workspace.phone || null,
    email: workspace.email || null,
    website: workspace.website || null,
  };
};

/**
 * Names to show for the signed-in member's workspace (Phase 4,
 * docs/plans/MULTI-TENANT-MODULES.md). Other companies see their own name;
 * the platform shows as "Sigma" only.
 */
export const useWorkspaceBranding = () => {
  const { identity } = useGetIdentity();
  const workspace = (identity as { workspace?: WorkspaceIdentity | null })
    ?.workspace;
  const isHome = workspace == null || workspace.is_home;
  return {
    isHome,
    workspaceName: isHome ? PRODUCT_FULL_NAME : workspace.name || PRODUCT_NAME,
    /** Short line under the product mark in the sidebar. */
    tagline: isHome ? null : workspace.name,
  };
};
